import { v4 as uuidv4 } from 'uuid'
import { FloorTable, Room } from '../types/floorPlan.model'
import { MIN_TABLE_SIZE } from '../utils/defaults'
import { FloorPlanRoomDto, FloorTableDto } from './FloorPlanAPI.model'

/**
 * Translates between the backend's nested floor-plan document and the flat
 * store shape the components render from.
 *
 *   backend: rooms[] each with tables[]         (table number, no roomId)
 *   store:   rooms[] (+ order) & tables[]        (table label, roomId)
 *
 * Keeping this translation here means the store, the canvas and every component
 * stay untouched when the backend is wired in.
 */

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const isUuid = (value: string) => UUID_RE.test(value)

// Backend requires each table `number` to be a unique integer in this range.
// PUT /floor-plan responds 400 "duplicate table numbers" otherwise.
const TABLE_NUMBER_MIN = 1
const TABLE_NUMBER_MAX = 32767

/** Flatten the backend document into the store's rooms + tables. */
export function floorPlanToStore(dto: FloorPlanRoomDto[]): {
  rooms: Room[]
  tables: FloorTable[]
} {
  const rooms: Room[] = []
  const tables: FloorTable[] = []

  dto.forEach((roomDto, index) => {
    // The backend always sends an id on read; fall back defensively.
    const roomId = roomDto.id ?? `room-${uuidv4()}`
    rooms.push({ id: roomId, name: roomDto.name, order: index })

    roomDto.tables?.forEach(tableDto => {
      tables.push({
        id: tableDto.id ?? `table-${uuidv4()}`,
        roomId,
        label: String(tableDto.number),
        shape: tableDto.shape,
        seatCount: tableDto.seatCount,
        x: tableDto.x,
        y: tableDto.y,
        width: tableDto.width,
        height: tableDto.height,
        rotation: tableDto.rotation
      })
    })
  })

  return { rooms, tables }
}

/**
 * Assigns every table a UNIQUE integer number for the backend.
 *
 * Table labels are normally plain numbers ("1", "2", …) and already unique, but
 * a user can rename a label to anything (empty, duplicate, non-numeric). Sending
 * a duplicate number makes PUT /floor-plan fail with 400. So we honour every
 * valid, in-range, not-yet-taken numeric label first, then fill the remaining
 * tables with the next free numbers, deterministically, so the same store state
 * always serialises identically (needed for dirty-checking).
 */
function assignTableNumbers(orderedTables: FloorTable[]): Map<string, number> {
  const numberByTableId = new Map<string, number>()
  const used = new Set<number>()

  orderedTables.forEach(table => {
    const parsed = parseInt(table.label, 10)
    if (
      Number.isInteger(parsed) &&
      parsed >= TABLE_NUMBER_MIN &&
      parsed <= TABLE_NUMBER_MAX &&
      !used.has(parsed)
    ) {
      used.add(parsed)
      numberByTableId.set(table.id, parsed)
    }
  })

  let next = TABLE_NUMBER_MIN
  const nextFreeNumber = () => {
    while (used.has(next) && next < TABLE_NUMBER_MAX) next += 1
    used.add(next)
    return next
  }

  orderedTables.forEach(table => {
    if (!numberByTableId.has(table.id)) {
      numberByTableId.set(table.id, nextFreeNumber())
    }
  })

  return numberByTableId
}

function toTableDto(table: FloorTable, number: number): FloorTableDto {
  // Spec: for round/square, width MUST equal height (both >= 40). Collapse to a
  // single side so a stray non-square resize can't be sent for those shapes.
  let width: number
  let height: number
  if (table.shape === 'rectangle') {
    width = Math.max(MIN_TABLE_SIZE, table.width)
    height = Math.max(MIN_TABLE_SIZE, table.height)
  } else {
    const side = Math.max(MIN_TABLE_SIZE, Math.min(table.width, table.height))
    width = side
    height = side
  }

  return {
    // Send the id only once the backend has issued a uuid (persisted table);
    // client-provisional ids ("table-<uuid>") are omitted so the backend creates.
    ...(isUuid(table.id) ? { id: table.id } : {}),
    number,
    shape: table.shape,
    seatCount: Math.max(0, Math.round(table.seatCount)),
    x: table.x,
    y: table.y,
    width,
    height,
    rotation: table.rotation
  }
}

/** Nest the store's flat rooms + tables back into the backend document. */
export function storeToFloorPlan(
  rooms: Room[],
  tables: FloorTable[]
): FloorPlanRoomDto[] {
  const sortedRooms = [...rooms].sort((a, b) => a.order - b.order)

  // Numbers must be unique across the WHOLE plan, so allocate in one global
  // pass (room order, then table order) before building the per-room payload.
  const orderedTables = sortedRooms.flatMap(room =>
    tables.filter(table => table.roomId === room.id)
  )
  const numberByTableId = assignTableNumbers(orderedTables)

  return sortedRooms.map(room => ({
    // Only send a uuid id, the seeded default rooms use non-uuid ids
    // ("room-main"), so omit those and let the backend assign one.
    ...(isUuid(room.id) ? { id: room.id } : {}),
    name: room.name,
    tables: tables
      .filter(table => table.roomId === room.id)
      .map(table => toTableDto(table, numberByTableId.get(table.id) ?? 1))
  }))
}

/**
 * Stable string form of the plan, used to detect unsaved changes (dirty state).
 * Built from the same payload we would PUT, so it reflects exactly what a save
 * would persist.
 */
export function serializePlan(rooms: Room[], tables: FloorTable[]): string {
  return JSON.stringify(storeToFloorPlan(rooms, tables))
}
